import React from "react"

import { AlertCircle, History, Loader2 } from "lucide-react"

import { ShiftAuditEntryCard } from "@/components/sheriff/shift/ShiftAuditEntryCard"
import {
  type AuditScope,
  getAuditEmptyStateMessage,
  getVisibleAuditChanges,
  isAuditScope,
} from "@/components/sheriff/shift/shiftAuditConfig"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { SHIFT_AUDIT_LOGS_PAGE_SIZE, useShiftAuditLogs } from "@/hooks/useShiftAuditLogs"
import { cn } from "@/lib/utils"

interface ShiftAuditDrawerProps {
  shiftId?: string
  timesheetId?: string
  open: boolean
  canView: boolean
  onClose: () => void
}

const ShiftAuditDrawer: React.FC<ShiftAuditDrawerProps> = ({
  shiftId,
  timesheetId,
  open,
  canView,
  onClose,
}) => {
  const [scope, setScope] = React.useState<AuditScope>("all")
  const [visibleCount, setVisibleCount] = React.useState(SHIFT_AUDIT_LOGS_PAGE_SIZE)
  const { data, isLoading, isError, error, isFetching } = useShiftAuditLogs({
    shiftId,
    timesheetId,
    enabled: canView && open,
    limit: visibleCount,
  })

  React.useEffect(() => {
    if (!open) {
      setScope("all")
      setVisibleCount(SHIFT_AUDIT_LOGS_PAGE_SIZE)
    }
  }, [open])

  React.useEffect(() => {
    setVisibleCount(SHIFT_AUDIT_LOGS_PAGE_SIZE)
  }, [shiftId, timesheetId])

  const filteredData = React.useMemo(() => {
    if (!data) return []

    return data.entries
      .filter((entry) => scope === "all" || entry.table_name === scope)
      .map((entry) => ({
        entry,
        changes: getVisibleAuditChanges(entry),
      }))
      .filter(({ changes }) => changes.length > 0)
  }, [data, scope])

  if (!canView) return null

  return (
    <aside
      className={cn(
        "absolute inset-y-0 right-0 z-10 overflow-hidden border-l bg-palette-jungleGreen000 shadow-2xl transition-all duration-300 md:static md:z-auto md:shadow-none",
        open
          ? "w-[320px] max-w-[85vw] translate-x-0 md:w-[380px] md:max-w-none"
          : "pointer-events-none w-[320px] max-w-[85vw] translate-x-full md:w-0 md:max-w-none md:translate-x-0",
      )}
      aria-hidden={!open}
    >
      <div className="flex h-full w-[320px] max-w-[85vw] flex-col md:w-[380px]">
        <div className="sticky top-0 z-10 border-b border-palette-jungleGreen200 bg-white px-4 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-palette-jungleGreen900 text-white">
                <History className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-palette-jungleGreen900">Changes</div>
                <div className="text-xs text-palette-jungleGreen500">
                  Block and timesheet audit trail
                </div>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
          <div className="mt-3">
            <Tabs
              value={scope}
              onValueChange={(value) => {
                if (isAuditScope(value)) {
                  setScope(value)
                }
              }}
              className="w-full"
            >
              <TabsList className="grid h-auto w-full grid-cols-3 bg-palette-jungleGreen100">
                <TabsTrigger value="all" className="px-2 text-xs">
                  All
                </TabsTrigger>
                <TabsTrigger value="shifts" className="px-2 text-xs">
                  Block
                </TabsTrigger>
                <TabsTrigger value="timesheets" className="px-2 text-xs">
                  Timesheet
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        <ScrollArea className="h-full">
          <div className="space-y-2.5 p-3">
            {isLoading ? (
              <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-sm text-palette-jungleGreen500">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading changes...</span>
              </div>
            ) : isError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex items-center gap-2 font-medium">
                  <AlertCircle className="h-4 w-4" />
                  Unable to load change history
                </div>
                <div className="mt-2 text-xs text-red-600">
                  {error instanceof Error ? error.message : "Unknown error"}
                </div>
              </div>
            ) : filteredData.length > 0 ? (
              <>
                {filteredData.map(({ entry, changes }) => (
                  <ShiftAuditEntryCard key={entry.id} entry={entry} changes={changes} />
                ))}
                {data?.hasMore ? (
                  <div className="pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        setVisibleCount((current) => current + SHIFT_AUDIT_LOGS_PAGE_SIZE)
                      }
                      disabled={isFetching}
                    >
                      {isFetching ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        "Load more"
                      )}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="rounded-lg border border-dashed border-palette-jungleGreen300 bg-white/70 p-6 text-center text-sm text-palette-jungleGreen500">
                  {getAuditEmptyStateMessage(scope)}
                </div>
                {data?.hasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() =>
                      setVisibleCount((current) => current + SHIFT_AUDIT_LOGS_PAGE_SIZE)
                    }
                    disabled={isFetching}
                  >
                    {isFetching ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load more"
                    )}
                  </Button>
                ) : null}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </aside>
  )
}

export default ShiftAuditDrawer
